import React, { useState, useEffect } from 'react';
import { BarChart3, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import ProcessingStatus, { dimClass } from '../components/ProcessingStatus';
import { Skel, TableSkeleton } from '../components/Skeleton';
import { useLatestRequest, isCanceled } from '../utils/latestRequest';

// the score table's columns — the real header and its loading shape
const SCORE_COLUMNS = ['Date', 'Open', 'Close', 'High', 'Low', 'MV Score', 'CB Score', 'Zone', 'AES Mod', 'AES Agg', 'Status'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const [y, m, d] = dateStr.substring(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  return dateStr;
};

const Indexes = () => {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [scoreHistory, setScoreHistory] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(false);

  useEffect(() => { fetchRegions(); }, []);

  useEffect(() => {
    if (selectedSymbol) fetchScoreHistory();
  }, [selectedSymbol, days]);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const res = await apiService.getRegions();
      setRegions(res.data);
      if (res.data.length > 0) {
        const saved = localStorage.getItem('indexesRegion');
        const region = res.data.find(r => r.code === saved) || res.data[0];
        setSelectedRegion(region);
        if (region.symbols?.length > 0) setSelectedSymbol(region.symbols[0]);
      }
    } catch { setError('Failed to load regions'); } finally { setLoading(false); }
  };

  const historyReq = useLatestRequest();
  const fetchScoreHistory = async () => {
    if (!selectedSymbol) return;
    const req = historyReq();       // a newer symbol / period click cancels this one
    try {
      setHistoryLoading(true);
      const res = await apiService.getScoreHistory({
        market: selectedSymbol.market_name,
        symbol: selectedSymbol.name,
        days,
      }, { signal: req.signal });
      if (req.isCurrent()) setScoreHistory(res.data);
    } catch (e) { if (req.isCurrent() && !isCanceled(e)) setScoreHistory(null); } finally { if (req.isCurrent()) setHistoryLoading(false); }
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    localStorage.setItem('indexesRegion', region.code);
    if (region.symbols?.length > 0) setSelectedSymbol(region.symbols[0]);
    else setSelectedSymbol(null);
  };

  const getZoneColor = (zone) => {
    if (zone === 'GREEN') return 'text-green-400';
    if (zone === 'RED') return 'text-red-400';
    return 'text-gray-400';
  };

  const getZoneBg = (zone) => {
    if (zone === 'GREEN') return 'bg-green-400/10';
    if (zone === 'RED') return 'bg-red-400/10';
    return 'bg-gray-400/10';
  };

  // Null cell renderer — red text for nulls
  const renderValue = (val, fieldName, nulls) => {
    if (val === null || val === undefined) {
      return <span className="text-red-400 font-semibold">NULL</span>;
    }
    if (typeof val === 'number') {
      return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return val;
  };

  // Count total nulls in data
  const nullSummary = scoreHistory?.data?.reduce((acc, row) => {
    if (row.nulls) row.nulls.forEach(n => { acc[n] = (acc[n] || 0) + 1; });
    return acc;
  }, {}) || {};
  const hasNulls = Object.keys(nullSummary).length > 0;

  // Filter rows if errors-only mode
  const displayData = errorsOnly
    ? (scoreHistory?.data || []).filter(row => row.nulls && row.nulls.length > 0)
    : (scoreHistory?.data || []);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-8 h-8 text-primary-400" />
              <h1 className="text-3xl font-bold text-white">Indexes</h1>
            </div>
            <p className="text-dark-300">View score history and data quality for all indexes</p>
          </div>
          {loading && <Skel className="h-[52px] w-[240px]" rounded="rounded-xl" testId="index-select-pending" />}
          {!loading && !error && (
            <select
              value={selectedSymbol ? `${selectedSymbol.market_name}:${selectedSymbol.name}` : ''}
              onChange={(e) => {
                const [mkt, sym] = e.target.value.split(':');
                for (const r of regions) {
                  const found = r.symbols.find(s => s.name === sym && s.market_name === mkt);
                  if (found) {
                    if (selectedRegion?.code !== r.code) handleRegionChange(r);
                    setSelectedSymbol(found);
                    break;
                  }
                }
              }}
              className="px-5 py-3 bg-dark-800 border-2 border-primary-500/50 rounded-xl text-white text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer min-w-[240px] shadow-lg"
            >
              {regions.map(r => (
                <optgroup key={r.code} label={r.name.toUpperCase()}>
                  {r.symbols.map(s => (
                    <option key={s.id} value={`${s.market_name}:${s.name}`}>{s.label || s.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="dashboard-card" data-testid="indexes-skeleton">
            <ProcessingStatus active title="Loading indexes" />
            <TableSkeleton rows={12} columns={SCORE_COLUMNS} />
          </div>
        ) : error ? (
          <div className="dashboard-card text-center py-8"><p className="text-red-400">{error}</p></div>
        ) : (
          <>
            {/* Days filter */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2 ml-auto">
                <button
                  onClick={() => setErrorsOnly(!errorsOnly)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    errorsOnly ? 'bg-red-600 text-white' : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
                  }`}
                >
                  Errors Only
                </button>
                <span className="text-dark-600">|</span>
                {[7, 14, 30, 60, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setDays(d)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      days === d ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
                    }`}
                  >
                    {d}D
                  </button>
                ))}
              </div>
            </div>

            {/* Content wrapper — fades during loading */}
            <div className={`space-y-6 ${dimClass(historyLoading && !!scoreHistory)}`}>

            {/* Null Summary Banner */}
            {hasNulls && !errorsOnly && (
              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-400 font-medium text-sm">Null values detected in {selectedSymbol?.name}</p>
                  <p className="text-red-300/70 text-xs mt-1">
                    {Object.entries(nullSummary).map(([field, count]) => `${field}: ${count}`).join(' · ')}
                  </p>
                </div>
              </div>
            )}

            {/* Data Table */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {selectedSymbol?.name || 'Select a symbol'} — {days} Days
                  {errorsOnly && <span className="ml-2 text-red-400 text-sm font-normal">({displayData.length} rows with issues)</span>}
                </h3>
              </div>
              <ProcessingStatus active={historyLoading} title="Loading score history" what={[selectedSymbol?.name, `${days} days`].filter(Boolean).join(' · ')} />

              {!scoreHistory && historyLoading ? (
                <TableSkeleton rows={12} columns={SCORE_COLUMNS} testId="history-skeleton" />
              ) : displayData.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-dark-400">
                    {errorsOnly ? 'No rows with null values found — data is clean' : 'No data available for the selected period'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-dark-700">
                        <th className="text-left text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Date</th>
                        <th className="text-right text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Open</th>
                        <th className="text-right text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Close</th>
                        <th className="text-right text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">High</th>
                        <th className="text-right text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Low</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">MV Score</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">CB Score</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Zone</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">AES Mod</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">AES Agg</th>
                        <th className="text-center text-xs font-semibold text-dark-400 uppercase tracking-wider py-3 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayData.map((row, idx) => {
                        const hasIssues = row.nulls && row.nulls.length > 0;
                        return (
                          <tr
                            key={idx}
                            className={`transition-colors ${
                              hasIssues
                                ? 'border-l-2 border-l-red-500 border-b border-b-dark-700/50 bg-red-900/8 hover:bg-red-900/15'
                                : 'border-l-2 border-l-transparent border-b border-b-dark-700/50 hover:bg-dark-700/30'
                            }`}
                          >
                            <td className="py-2 px-3 text-sm text-white font-mono">{formatDate(row.date)}</td>
                            <td className={`py-2 px-3 text-sm text-right font-mono ${row.open === null ? 'text-red-400' : 'text-dark-300'}`}>
                              {renderValue(row.open, 'open', row.nulls)}
                            </td>
                            <td className={`py-2 px-3 text-sm text-right font-mono ${row.close === null ? 'text-red-400' : 'text-white'}`}>
                              {renderValue(row.close, 'close', row.nulls)}
                            </td>
                            <td className={`py-2 px-3 text-sm text-right font-mono ${row.high === null ? 'text-red-400' : 'text-dark-300'}`}>
                              {renderValue(row.high, 'high', row.nulls)}
                            </td>
                            <td className={`py-2 px-3 text-sm text-right font-mono ${row.low === null ? 'text-red-400' : 'text-dark-300'}`}>
                              {renderValue(row.low, 'low', row.nulls)}
                            </td>
                            <td className={`py-2 px-3 text-center ${row.mv_score === null ? '' : ''}`}>
                              {row.mv_score === null
                                ? <span className="text-red-400 font-semibold text-sm">NULL</span>
                                : <span className={`text-sm font-bold ${getZoneColor(_getZone(row.mv_score))}`}>{row.mv_score}</span>
                              }
                            </td>
                            <td className="py-2 px-3 text-center">
                              {row.cb_score === null
                                ? <span className="text-dark-500 text-xs">—</span>
                                : <span className={`text-sm font-bold ${getZoneColor(_getZone(row.cb_score))}`}>{row.cb_score}</span>
                              }
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getZoneBg(row.zone)} ${getZoneColor(row.zone)}`}>
                                {row.zone === 'GREEN' ? 'BULLISH' : row.zone === 'RED' ? 'BEARISH' : row.zone === 'GREY' ? 'NEUTRAL' : '-'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-sm text-center text-dark-300">
                              {row.aes_leverage_moderate ?? <span className="text-dark-500">—</span>}
                            </td>
                            <td className="py-2 px-3 text-sm text-center text-dark-300">
                              {row.aes_leverage_aggressive ?? <span className="text-dark-500">—</span>}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {hasIssues
                                ? (
                                  <span className="inline-flex items-center gap-1 text-red-400" title={`Missing: ${row.nulls.join(', ')}`}>
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-xs">{row.nulls.length}</span>
                                  </span>
                                )
                                : <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>{/* end transition wrapper */}
          </>
        )}
      </div>
    </Layout>
  );
};

// Helper to derive zone from score (same as backend)
function _getZone(score) {
  if (score === null || score === undefined) return null;
  if (score >= 71) return 'GREEN';
  if (score <= 30) return 'RED';
  return 'GREY';
}

export default Indexes;
